/**
 * ROROS Meetings — Frontend Types (M-Rhythm-2)
 *
 * These mirror the server-side DTOs from meetings.types.ts.
 * Meetings are NOT work items — they never affect WIP capacity.
 */

export type MeetingType =
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'trimester'
  | 'annual'
  | 'ad_hoc';

export type MeetingStatus =
  | 'scheduled'
  | 'in_progress'
  | 'paused'
  | 'completed'
  | 'skipped'
  | 'cancelled';

/**
 * Facilitation state stored on a meeting once start-facilitation is called.
 * Mirrors server-side FacilitationState interface.
 */
export interface FacilitationState {
  currentAgendaItemId: string | null;
  startedAt: string;
  lastActiveAt: string;
  completedItems: Array<{ itemId: string; response: unknown; completedAt: string }>;
  /**
   * AC-4A-2: Draft response for the current step saved on "Save & Pause".
   * Verbatim (empty string is valid per OQ-2).
   * Set to null (not omitted) when the item advances or the meeting resumes,
   * so a stale draft cannot restore into a later step.
   */
  currentItemDraft?: unknown | null;
}

/**
 * Extended agenda item shape returned from the facilitation endpoints.
 * Adds fields present in structured cadence agenda items (input_type, prompt, etc.)
 */
export interface AgendaItemWithFacilitation {
  id: string;
  orgId: string;
  meetingId: string;
  sortOrder: number;
  title: string;
  /** Locale-specific title (English). Falls back to title if not present. */
  titleEn?: string | null;
  /** Prompt / question text shown to the facilitator. */
  prompt?: string | null;
  /** Determines which input UI to render for this agenda item. */
  inputType?: 'state_recheck' | 'outcome_disposition_table' | 'annual_goal_text' | 'outcome_text_per_goal' | 'confirm_cascade' | string;
  accountableActorId: string | null;
  accountableDomainId: string | null;
  durationMinutes: number | null;
  createdAt: string;
  updatedAt: string;
}

export type ParticipantKind = 'actor' | 'domain_accountable';
export type ParticipantRole = 'attendee' | 'observer';

export interface RorosMeetingParticipantDTO {
  id: string;
  orgId: string;
  meetingId: string;
  participantKind: ParticipantKind;
  actorId: string | null;
  domainId: string | null;
  role: ParticipantRole;
  displayName: string | null;
  createdAt: string;
}

export interface RorosMeetingAgendaItemDTO {
  id: string;
  orgId: string;
  meetingId: string;
  sortOrder: number;
  title: string;
  accountableActorId: string | null;
  accountableDomainId: string | null;
  durationMinutes: number | null;
  /** F-Meeting-1 Wave 2: structured input type for facilitation wizard rendering */
  inputType: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RorosMeetingDTO {
  id: string;
  orgId: string;
  cadenceScheduleId: string | null;
  meetingType: MeetingType;
  title: string;
  scheduledAt: string;
  durationMinutes: number | null;
  accountableActorId: string | null;
  accountableDomainId: string | null;
  moderatorActorId: string | null;
  status: MeetingStatus;
  skippedReason: string | null;
  tags: string[];
  isRecurringException: boolean;
  /** Wave 10: ID of the higher-cadence meeting that absorbed this one. NULL = not absorbed. */
  absorbedByMeetingId: string | null;
  /** Wave 4B: fields on this cadence-tied instance that were manually overridden by the user. */
  manualFieldOverrides: string[];
  participants: RorosMeetingParticipantDTO[];
  agendaItems: RorosMeetingAgendaItemDTO[];
  /** F-Meeting-1 Wave 2: Facilitation state, present once start-facilitation is called. */
  facilitationState?: FacilitationState | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Virtual occurrence: a daily check-in that has not yet been materialized
 * into roros_meetings. Generated on-the-fly by listMeetingsInRange.
 */
export interface VirtualMeetingDTO {
  id: null;
  isVirtual: true;
  orgId: string;
  cadenceScheduleId: string;
  meetingType: 'daily';
  title: string;
  scheduledAt: string;
  durationMinutes: number | null;
  accountableActorId: string | null;
  accountableDomainId: null;
  moderatorActorId: string | null;
  status: 'scheduled';
  skippedReason: null;
  tags: [];
  isRecurringException: false;
  participants: RorosMeetingParticipantDTO[];
  agendaItems: RorosMeetingAgendaItemDTO[];
  createdAt: null;
  updatedAt: null;
}

// CB-002: Intentional parallel — server/modules/meetings/meetings.types.ts is source of truth.
// Keep in sync with the server definition when updating the DTO shape.
/** Union type returned by the calendar endpoint */
export type CalendarMeetingDTO = RorosMeetingDTO | VirtualMeetingDTO;

/** Type guard for virtual meetings */
export function isVirtualMeeting(m: CalendarMeetingDTO): m is VirtualMeetingDTO {
  return (m as VirtualMeetingDTO).isVirtual === true;
}

/** Calendar endpoint response shape */
export interface CalendarResponse {
  meetings: CalendarMeetingDTO[];
}

/**
 * AC-3b-1-5: Outcome pending disposition from GET /api/v1/outcomes/pending-disposition.
 * Used by the Annual wizard's disposition step (MeetingWizardPage.tsx).
 */
export interface PendingOutcome {
  id: string;
  title: string;
  status: string;
  time_horizon: string;
  domain_name: string | null;
}
